import React, { useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import {
  Button,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Chip,
  IconButton,
  Collapse,
} from "@mui/material";
import { Form, Field } from "react-final-form";
import { Autocomplete } from "mui-rff";
import styles from "./VideoManager.module.scss";
import debounce from "lodash.debounce";
import ReactPlayer from "react-player";
import { toast } from "react-toastify";
import {
  Clear as ClearIcon,
  Search as SearchIcon,
  Add as AddIcon,
  ExpandLess,
  ExpandMore,
  SettingsOutlined,
  DeleteOutline,
  Language,
  Person2,
  Groups2,
  PlayArrow,
} from "@mui/icons-material";
import { confirmAlert } from "react-confirm-alert";

interface VideoItem {
  id?: number | string;
  title: string;
  url: string;
  thumbnail?: string;
  description?: string;
  platform?: string;
  duration?: string;
  type?: string;
}

interface PlaylistItem {
  id: number | string;
  name: string;
  type: string;
  level: string;
  treatment_category_id?: number | string | null;
  treatment_ids?: number[];
  is_default?: boolean;
  videos: VideoItem[];
}

const getLevelLabel = (level?: string) => {
  if (level === "global") return "Global";
  if (level === "practice") return "Practice";
  return "User";
};

const getPlaylistTypeIcon = (type?: string) => {
  if (type === "global_playlists") return <Language className={styles.categoryIcon} />;
  if (type === "default_practice_playlists") return <Groups2 className={styles.categoryIcon} />;
  return <Person2 className={styles.categoryIcon} />;
};

export default function VideoManager() {
  const [categories, setCategories] = useState<PlaylistItem[]>([]);
  const [currentCategory, setCurrentCategory] = useState<PlaylistItem | null>(null);
  const [currentVideo, setCurrentVideo] = useState<VideoItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [treatments, setTreatments] = useState<any[]>([]);
  const [selectedTreatment, setSelectedTreatment] = useState("");
  const [userid, setUserId] = useState("");
  const [practiceId, setPracticeId] = useState("");
  const [openVideoModal, setOpenVideoModal] = useState(false);
  const [openPlaylistModal, setOpenPlaylistModal] = useState(false);
  const [newVideoForm, setNewVideoForm] = useState({
    title: "",
    url: "",
    thumbnail: "",
    platform: "youtube",
    duration: "",
    description: "",
  });
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | string | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [lastPlayedSeconds, setLastPlayedSeconds] = useState<number | null>(null);
  const [playedBeforePause, setPlayedBeforePause] = useState<number | null>(null);
  const [level, setLevel] = useState("");
  const [isArchived, setIsArchived] = useState(false);
  const [itemId, setItemId] = useState<number | string>("");
  const [filter, setFilter] = useState<string>("all");
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState<number[]>([]);
  const [selectedTreatmentIdsNew, setSelectedTreatmentIdsNew] = useState<number[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(true);

  const startTimeRef = useRef<number | null>(null);
  const playbackTimeRef = useRef(0);

  const { data: playlistCategories } = useQuery(["playlistCategories"], async () => {
    const { data } = await axios.post(`/treatment_category`);
    return data;
  });

  const [treatmentPlans, setTreatmentPlans] = useState<any[]>([]);

  useEffect(() => {
    const fetchTreatmentPlans = async () => {
      try {
        const { data } = await axios.get(`/treatment`);
        const plans = data.treatments.map((plan: any) => ({
          ...plan,
          label: plan.description,
          value: plan.id,
        }));
        setTreatmentPlans(plans);
      } catch (error) {
        console.error("Error fetching treatment plans:", error);
      }
    };

    fetchTreatmentPlans();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const treatmentResponse = await axios.get("/treatment_category");
        const treatmentOptions = treatmentResponse.data.map((plan: any) => ({
          label: plan.category_name,
          value: plan.id,
        }));
        setTreatments(treatmentOptions);

        const userResponse = await axios.get("/user");
        setUserId(userResponse.data[1].medicube_user_id);
        setPracticeId(userResponse.data[1].medicube_practice_id);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    handleData(undefined, "");
    fetchData();
  }, []);

  useEffect(() => {
    if (currentCategory?.treatment_ids) {
      setSelectedTreatmentIds(currentCategory.treatment_ids);
    }
  }, [currentCategory]);

  const normalizePlaylistData = (responseData: any) => {
    const {
      default_user_playlists = [],
      default_practice_playlists = [],
      global_playlists = [],
      non_default_playlists = [],
      treatment_categories = [],
    } = responseData;

    const allPlaylists = [
      ...default_user_playlists.map((item: any) => ({ ...item, type: "default_user_playlists" })),
      ...default_practice_playlists.map((item: any) => ({ ...item, type: "default_practice_playlists" })),
      ...global_playlists.map((item: any) => ({ ...item, type: "global_playlists" })),
      ...non_default_playlists.map((item: any) => ({ ...item, type: "non_default_playlists" })),
    ];

    const formatted = allPlaylists.map((item: any) => {
      const videos = item.playist_items || [];
      const firstVideoTitle = videos.length > 0 ? videos[0].title || `Playlist ${item.id}` : `Playlist ${item.id}`;

      return {
        id: item.id,
        name: item.title || firstVideoTitle,
        type: item.type,
        level: item.level,
        treatment_category_id: item.treatment_category_id,
        treatment_ids: item.treatment_ids || [],
        is_default: item.is_default,
        videos: videos.map((video: any) => ({
          id: video.id,
          title: video.title,
          url: video.url,
          thumbnail: video.thumbnail,
          description: video.description,
          platform: video.platform,
          duration: video.duration,
          type: video.type,
        })),
      } as PlaylistItem;
    });

    return {
      formatted,
      treatment_categories,
    };
  };

  const setInitialSelection = (formattedData: PlaylistItem[], treatmentCategories: any[]) => {
    if (!formattedData.length) {
      setCurrentCategory(null);
      setCurrentVideo(null);
      setSelectedCategoryId(null);
      setSelectedCategoryName("");
      return;
    }

    const first = formattedData[0];
    setCurrentCategory(first);
    setSelectedCategoryId(first.id);
    setCurrentVideo(first.videos?.[0] || null);
    setItemId(first.id);
    setLevel(first.level);

    const selectedCategory = treatmentCategories.find(
      (cat: any) => cat.id === first.treatment_category_id
    );
    setSelectedCategoryName(selectedCategory ? selectedCategory.category_name || selectedCategory.name : "Unknown Category");
  };

  const handleData = (category?: number, search?: string) => {
    setLoading(true);
    axios
      .get(`/playlist/`, { params: { category, search } })
      .then((response) => {
        const { formatted, treatment_categories } = normalizePlaylistData(response.data);
        setCategories(formatted);
        setInitialSelection(formatted, treatment_categories);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching the playlist data:", error);
        setLoading(false);
      });
  };

  const logUserAction = (event: string, description: string, data: any) => {
    if (!userid) return;

    axios
      .post(`/log`, {
        type: "info",
        description,
        event,
        data,
        practice_id: practiceId,
        user_id: userid,
      })
      .catch((error) => {
        console.error("Error logging user action:", error);
      });
  };

  const resetTimeTracking = () => {
    startTimeRef.current = Date.now();
    playbackTimeRef.current = 0;
  };

  const logCurrentVideoTime = () => {
    if (startTimeRef.current && currentVideo) {
      const endTime = Date.now();
      const duration = (endTime - startTimeRef.current) / 1000;
      const actualPlaybackTime = playbackTimeRef.current;

      logUserAction("Video Watched", "User stopped watching a video", {
        video: currentVideo.title,
        duration,
        actualPlaybackTime,
      });
    }
  };

  const handleVideoStart = () => resetTimeTracking();

  const handleVideoProgress = (state: any) => {
    setLastPlayedSeconds(state.playedSeconds);
    playbackTimeRef.current = state.playedSeconds;
  };

  const handleVideoPause = () => {
    setPlayedBeforePause(lastPlayedSeconds);
    logCurrentVideoTime();
    resetTimeTracking();
  };

  const handleVideoResume = () => {
    const _calculatedSeekTime = (lastPlayedSeconds || 0) - (playedBeforePause || 0);
    void _calculatedSeekTime;
  };

  const handleVideoEnd = () => {
    logCurrentVideoTime();
    resetTimeTracking();
  };

  const onCategorySelect = async (category: PlaylistItem) => {
    setSelectedCategoryId(category.id);
    try {
      const response = await axios.get(`/playlist/${category.id}`);
      setLevel(response.data.level);
      setItemId(response.data.id);
      setIsArchived(!!response.data.is_archived);
      setSelectedCategoryName(response.data.treatment_category_name || "Unknown Category");
      setCurrentCategory(category);
      setCurrentVideo(category.videos.length > 0 ? category.videos[0] : null);

      logUserAction("Treatment Selected", "User clicked on a treatment", {
        treatment: category.name,
      });
    } catch (error) {
      console.error("Error fetching playlist:", error);
    }
  };

  const onVideoSelect = (video: VideoItem) => {
    logCurrentVideoTime();
    setCurrentVideo(video);
    logUserAction("Video Selected", "User clicked on a Video", { video: video.title });
    resetTimeTracking();
  };

  const updatePlaylist = async (values: any) => {
    if (!currentCategory) return;

    try {
      const payload = {
        title: values.playlistName,
        treatment_category_id: values.category_id,
        treatment_ids: selectedTreatmentIds,
        level: values.options,
      };

      await axios.put(`/playlist/${currentCategory.id}`, payload);
      const getResponse = await axios.get(`/playlist/${currentCategory.id}`);

      setLevel(getResponse.data.level);
      setItemId(getResponse.data.id);
      setIsArchived(getResponse.data.is_archived);
      setSelectedCategoryName(getResponse.data.treatment_category_name);

      handleData();
      toast.success("Playlist edited successfully");
    } catch (error: any) {
      if (error.response && error.response.status === 400) {
        toast.error(`Error: ${error.response.data.message || "Playlist template name is not unique."}`);
      } else {
        console.error("Error updating playlist:", error);
      }
    }
  };

  const clonePlaylist = async (values: any) => {
    if (!currentCategory) return;

    try {
      if (!values.treatment_ids || values.treatment_ids.length === 0) {
        toast.error("Please select at least one treatment.");
        return;
      }

      if (!values.options || values.options.trim() === "") {
        toast.error("Please select a level.");
        return;
      }

      const payload = {
        title: values.playlistName,
        treatment_category_id: values.category_id,
        treatment_ids: values.treatment_ids.map((t: any) => t.value),
        level: values.options,
      };

      const response = await axios.put(
        `/playlist/${currentCategory.id}/copy_global_playlist_to_private`,
        payload
      );

      const newPlaylist: PlaylistItem = {
        id: response.data.id,
        name: values.playlistName,
        videos: response.data.playist_items.map((video: any) => ({
          id: video.id,
          title: video.title,
          url: video.url,
          thumbnail: video.thumbnail || "",
          description: video.description,
          type: video.type,
          platform: video.platform,
          duration: video.duration,
        })),
        level: response.data.level,
        type: response.data.level === "practice" ? "default_practice_playlists" : "default_user_playlists",
        is_default: response.data.is_default,
        treatment_category_id: response.data.treatment_category_id,
        treatment_ids: response.data.treatment_ids || [],
      };

      setItemId(newPlaylist.id);
      setCategories((prev) => [newPlaylist, ...prev]);
      setCurrentCategory(newPlaylist);
      setSelectedCategoryId(newPlaylist.id);
      setCurrentVideo(newPlaylist.videos[0] || null);
      toast.success("Playlist created successfully");
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "An error occurred while cloning the playlist.");
      console.error("Error cloning playlist:", error);
    }
  };

  const handleSubmitPlaylistUpdate = (values: any) => {
    if (!currentCategory) return;
    if (currentCategory.level === "global") clonePlaylist(values);
    else updatePlaylist(values);
  };

  const handleSubmitPlaylistSave = async (formValues: any) => {
    try {
      const { playlistName, category_id } = formValues;
      const categoryIdInt = parseInt(category_id, 10);

      const response = await axios.post("/playlist/", {
        title: playlistName,
        treatment_category_id: categoryIdInt,
        treatment_ids: selectedTreatmentIdsNew,
      });

      const newPlaylist: PlaylistItem = {
        id: response.data,
        name: playlistName,
        treatment_category_id: categoryIdInt,
        videos: [],
        level: "user",
        type: "default_user_playlists",
      } as PlaylistItem;

      const getResponse = await axios.get(`/playlist/${newPlaylist.id}`);
      setItemId(getResponse.data.id);
      setCategories((prev) => [newPlaylist, ...prev]);
      setCurrentCategory(newPlaylist);
      setSelectedCategoryId(newPlaylist.id);
      setSelectedCategoryName(getResponse.data.treatment_category_name);
      setSelectedTreatmentIds(getResponse.data.treatment_ids);
      setCurrentVideo(null);
      toast.success("Playlist created successfully");
      setOpenPlaylistModal(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Error saving playlist.");
      console.error("Error saving playlist:", error);
    }
  };

  const fetchPlaylistsByTreatment = (treatmentId: string) => {
    setLoading(true);
    axios
      .get(`/playlist/?treatment_category_id=${treatmentId}`)
      .then((response) => {
        const { formatted, treatment_categories } = normalizePlaylistData(response.data);
        setCategories(formatted);
        setInitialSelection(formatted, treatment_categories);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching the playlist data:", error);
        setLoading(false);
      });
  };

  const handleSearch = useCallback(
    debounce((value: string) => {
      if (value.trim() === "") {
        handleData();
        return;
      }

      setSearchLoading(true);
      axios
        .get(`/playlist/?search=${value}`)
        .then((response) => {
          const { formatted, treatment_categories } = normalizePlaylistData(response.data);
          setCategories(formatted);
          setInitialSelection(formatted, treatment_categories);
          setSearchLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching the playlist data:", error);
          setSearchLoading(false);
        });
    }, 300),
    []
  );

  useEffect(() => {
    handleSearch(searchParam);
  }, [searchParam, handleSearch]);

  useEffect(() => {
    if (selectedTreatment) fetchPlaylistsByTreatment(selectedTreatment);
  }, [selectedTreatment]);

  const handleTreatmentChange = (_event: any, newValue: any[]) => {
    setSelectedTreatmentIds(newValue.map((option) => option.value));
  };

  const handleTreatmentChangeNew = (_event: any, newValue: any[]) => {
    setSelectedTreatmentIdsNew(newValue.map((option) => option.value));
  };

  const handleSubmitPlaylistItemSave = async (newVideo: VideoItem) => {
    try {
      if (!currentCategory) return;
      const updatedCategory = {
        ...currentCategory,
        videos: [...currentCategory.videos, newVideo],
      };

      setCategories((prev) => prev.map((cat) => (cat.id === updatedCategory.id ? updatedCategory : cat)));
      setCurrentCategory(updatedCategory);
      setCurrentVideo(newVideo);
      setOpenVideoModal(false);
    } catch (error) {
      toast.error("Error saving playlist item.");
      console.error("Error saving playlist item:", error);
    }
  };

  const handleDeletePlaylist = (playlistId: number | string) => {
    axios
      .delete(`/playlist/${playlistId}`)
      .then(() => {
        setCategories((prev) => prev.filter((category) => category.id !== playlistId));
        if (currentCategory?.id === playlistId) {
          setCurrentCategory(null);
          setCurrentVideo(null);
        }
        handleData();
        toast.success("Playlist deleted successfully");
      })
      .catch((error) => {
        toast.error("Error deleting playlist.");
        console.error("Error deleting playlist:", error);
      });
  };

  const handleDeleteVideo = async (videoToDelete: VideoItem) => {
    if (!currentCategory) return;

    try {
      const updatedVideos = currentCategory.videos.filter((video) => video !== videoToDelete);
      const payload = updatedVideos.map(({ id, thumbnail, ...video }: any) => ({
        title: video.title || "",
        description: video.description || "",
        url: video.url,
        platform: video.platform || "s3",
        duration: video.duration || "00:00:00",
        type: video.type || "mp4",
      }));

      const response = await axios.put(`playlist/${itemId}/save_playlist_items`, payload);
      if (response.status === 200 && response.data === true) {
        const updatedCategory = { ...currentCategory, videos: updatedVideos };
        setCategories((prev) => prev.map((category) => (category.id === updatedCategory.id ? updatedCategory : category)));
        setCurrentCategory(updatedCategory);
        setCurrentVideo(updatedVideos[0] || null);
        toast.success("Playlist item deleted successfully");
      } else {
        toast.error("Failed to delete playlist item.");
      }
    } catch (error) {
      console.error("Error deleting playlist item:", error);
      toast.error("An error occurred while deleting the playlist item.");
    }
  };

  const extractTikTokId = (url?: string | null) => {
    if (!url) return null;
    const match = url.match(/video\/(\d+)/);
    return match ? match[1] : null;
  };

  const getFilteredPlaylists = () => {
    if (filter === "all") return categories;
    if (filter === "default_user_playlists") return categories.filter((cat) => cat.type === "default_user_playlists");
    if (filter === "default_practice_playlists") return categories.filter((cat) => cat.type === "default_practice_playlists");
    if (filter === "global_playlists") return categories.filter((cat) => cat.type === "global_playlists");
    if (filter === "non_default_playlists") return categories.filter((cat) => cat.type === "non_default_playlists");
    return categories;
  };

  const filteredPlaylists = getFilteredPlaylists();

const handleCreateVideo = async () => {
  if (!currentCategory) return;

  const title = newVideoForm.title.trim();
  const url = newVideoForm.url.trim();
  const thumbnail = newVideoForm.thumbnail.trim();
  const description = newVideoForm.description.trim();
  const duration = newVideoForm.duration.trim();
  const platform = (newVideoForm.platform || "youtube").trim();

  if (!title) {
    toast.error("Video title is required.");
    return;
  }

  if (!url) {
    toast.error("Video URL is required.");
    return;
  }

  try {
    const newVideo: VideoItem = {
      title,
      url,
      thumbnail,
      description,
      platform,
      duration: duration || "00:00:00",
      type: platform === "s3" ? "mp4" : "link",
    };

    const payload = [...(currentCategory.videos || []), newVideo].map(
      (video: any) => ({
        title: video.title || "",
        description: video.description || "",
        url: video.url,
        thumbnail: video.thumbnail || "",
        platform: video.platform || "youtube",
        duration: video.duration || "00:00:00",
        type: video.type || (video.platform === "s3" ? "mp4" : "link"),
      })
    );

    const response = await axios.put(
      `/playlist/${itemId}/save_playlist_items`,
      payload
    );

    if (response.status === 200 && response.data === true) {
      await handleSubmitPlaylistItemSave(newVideo);

      setNewVideoForm({
        title: "",
        url: "",
        thumbnail: "",
        platform: "youtube",
        duration: "",
        description: "",
      });

      toast.success("Video added successfully");
    } else {
      toast.error("Failed to add video.");
    }
  } catch (error) {
    console.error("Error creating video:", error);
    toast.error("An error occurred while adding the video.");
  }
};


  if (loading) {
    return (
      <div className={styles.videoLoadingContainer}>
        {/* <div className={styles.videoLoadingCard}>
          <Lottie animationData={videoLoadingAnimation} style={{ width: 240, height: 240 }} />
          <img src="/logo.png" alt="Overlay image" className={styles.loadingLogo} />
          <p className={styles.loadingText}>Loading your video library…</p>
        </div> */}
      </div>
    );
  }

  return (
    <>
      <Box className={styles.appShell}>
        <div className={styles.pageTopBar}>
          <h1 className={styles.pageTitle}>Video Gallery</h1>
          <Button className={styles.topActionButton} variant="contained" onClick={() => setOpenPlaylistModal(true)} startIcon={<AddIcon />}>
            New Playlist
          </Button>
        </div>

        <Dialog
          open={openPlaylistModal}
          onClose={() => setOpenPlaylistModal(false)}
          PaperProps={{ className: styles.dialogPaper }}
        >
          <DialogTitle className={styles.dialogTitle}>Create New Playlist</DialogTitle>
          <DialogContent>
            <Form
              onSubmit={handleSubmitPlaylistSave}
              render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit} className={styles.dialogForm}>
                  <Field name="playlistName">
                    {({ input }) => (
                      <TextField label="Playlist name" fullWidth size="small" {...input} />
                    )}
                  </Field>

                  <Field name="category_id">
                    {({ input }) => (
                      <TextField select label="Category" fullWidth size="small" {...input}>
                        {playlistCategories?.map((category: any) => (
                          <MenuItem value={category.id} key={category.id}>
                            {category.category_name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Field>

                  <Autocomplete
                    name="treatment_idsnew"
                    label="Linked treatments"
                    multiple
                    required
                    options={treatmentPlans}
                    getOptionLabel={(option: any) => option.label}
                    getOptionSelected={(option: any, value: any) => option.value === value}
                    onChange={handleTreatmentChangeNew}
                    renderInput={(params) => <TextField {...params} label="Linked treatments" size="small" />}
                  />

                  <DialogActions className={styles.dialogActions}>
                    <Button onClick={() => setOpenPlaylistModal(false)} variant="outlined">Cancel</Button>
                    <Button type="submit" variant="contained">Create Playlist</Button>
                  </DialogActions>
                </form>
              )}
            />
          </DialogContent>
        </Dialog>

        <Grid container spacing={2.5} className={styles.layoutGrid}>
          <Grid item xs={12} md={3}>
            <aside className={styles.sidebarCard}>
              <div className={styles.sidebarFilters}>
                <div className={styles.searchFieldWrap}>
                  <SearchIcon className={styles.searchIcon} />
                  <input
                    className={styles.searchInput}
                    placeholder="Search..."
                    value={searchParam}
                    onChange={(e) => setSearchParam(e.target.value)}
                  />
                </div>

                <Select
                  value={selectedTreatment}
                  onChange={(e) => setSelectedTreatment(e.target.value as string)}
                  displayEmpty
                  size="small"
                  className={styles.filterSelect}
                  fullWidth
                >
                  <MenuItem value="">All</MenuItem>
                  {treatments.map((treatment) => (
                    <MenuItem key={treatment.value} value={treatment.value}>
                      {treatment.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              {(searchParam || selectedTreatment || filter !== "all") && (
                <button
                  type="button"
                  className={styles.clearFiltersBtn}
                  onClick={() => {
                    setSearchParam("");
                    setSelectedTreatment("");
                    setFilter("all");
                    handleData();
                  }}
                >
                  <ClearIcon fontSize="small" /> Clear filters
                </button>
              )}

              {searchLoading && (
                <div className={styles.sidebarLoader}>
                  <CircularProgress size={18} />
                </div>
              )}

              <div className={styles.sidebarList}>
                {filteredPlaylists.length === 0 ? (
                  <div className={styles.emptyStateText}>No playlists found</div>
                ) : (
                  filteredPlaylists.map((category) => (
                    <button
                      type="button"
                      key={category.id}
                      className={`${styles.sidebarItem} ${category.id === selectedCategoryId ? styles.sidebarItemActive : ""}`}
                      onClick={() => onCategorySelect(category)}
                    >
                      <div className={styles.sidebarItemHeader}>
                        <div className={styles.sidebarItemTextWrap}>
                          <div className={styles.sidebarItemTitle}>{category.name}</div>
                          <div className={styles.sidebarItemMeta}>
                            {selectedCategoryName || getLevelLabel(category.level)} · {category.videos?.length || 0} videos
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </aside>
          </Grid>

          <Grid item xs={12} md={9}>
            <div className={styles.contentColumn}>
              <section className={styles.playerCard}>
                <div className={styles.playerStage}>
                  {currentVideo?.platform === "tiktok" ? (
                    <iframe
                      id="tiktok-player"
                      src={`https://www.tiktok.com/player/v1/${extractTikTokId(currentVideo.url)}?music_info=1&description=1`}
                      width="100%"
                      height="520"
                      allow="autoplay; fullscreen"
                      title="TikTok Player"
                      className={styles.videoFrame}
                    />
                  ) : currentVideo ? (
                    <ReactPlayer
                      url={currentVideo.url}
                      controls
                      width="100%"
                      height="520px"
                      onStart={handleVideoStart}
                      onProgress={handleVideoProgress}
                      onPause={handleVideoPause}
                      onEnded={handleVideoEnd}
                      onPlay={handleVideoResume}
                      className={styles.reactPlayer}
                    />
                  ) : (
                    <div className={styles.noVideoState}>
                      <PlayArrow className={styles.noVideoIcon} />
                      <p>No videos in this playlist yet.</p>
                      {level !== "global" && !isArchived && (
                        <Button variant="contained" onClick={() => setOpenVideoModal(true)}>
                          Add first video
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </section>

              <section className={styles.playlistPanel}>
                <div className={styles.playlistPanelHeader}>
                  <div className={styles.playlistPanelTitleWrap}>
                    <h2 className={styles.playlistPanelTitle}>{currentCategory?.name || "Playlist"}</h2>
                    <span className={styles.videoCountBadge}>{currentCategory?.videos?.length || 0} videos</span>
                  </div>

                  {level !== "global" && !isArchived && (
                    <Button className={styles.addVideoButton} variant="contained" startIcon={<AddIcon />} onClick={() => setOpenVideoModal(true)}>
                      Add Video
                    </Button>
                  )}
                </div>

                <div className={styles.videoList}>
                  {currentCategory?.videos?.length ? (
                    currentCategory.videos.map((video, index) => (
                      <div
                        key={`${video.url}-${index}`}
                        className={`${styles.videoRow} ${video === currentVideo ? styles.videoRowActive : ""}`}
                        onClick={() => onVideoSelect(video)}
                      >
                        <div className={styles.videoThumbWrap}>
                          {video.thumbnail ? (
                            <img src={video.thumbnail} alt={video.title} className={styles.videoThumb} />
                          ) : (
                            <div className={styles.videoThumbPlaceholder}>
                              <PlayArrow />
                            </div>
                          )}
                        </div>

                        <div className={styles.videoRowContent}>
                          <div className={styles.videoRowTitle}>{video.title}</div>
                          <div className={styles.videoRowMeta}>
                            {(video.platform || "youtube").toLowerCase()} {video.duration ? ` ${video.duration}` : ""}
                          </div>
                        </div>

                        {level !== "global" && !isArchived && (
                          <IconButton
                            size="small"
                            className={styles.deleteVideoBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteVideo(video);
                            }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className={styles.emptyVideosText}>No videos added yet.</div>
                  )}
                </div>
              </section>

              <section className={styles.settingsCard}>
                <button type="button" className={styles.settingsHeader} onClick={() => setSettingsOpen((prev) => !prev)}>
                  <div className={styles.settingsHeaderLeft}>
                    <SettingsOutlined fontSize="small" />
                    <span>Playlist Settings</span>
                  </div>
                  {settingsOpen ? <ExpandLess /> : <ExpandMore />}
                </button>

                <Collapse in={settingsOpen}>
                  {currentCategory && (
                    <Form
                      onSubmit={handleSubmitPlaylistUpdate}
                      initialValues={{
                        playlistName: currentCategory.name,
                        category_id: currentCategory.treatment_category_id || "",
                        options: currentCategory.level === "practice" ? "practice" : "user",
                        treatment_ids: treatmentPlans.filter((item) => selectedTreatmentIds.includes(item.value)),
                      }}
                      render={({ handleSubmit, values }) => (
                        <form onSubmit={handleSubmit} className={styles.settingsForm}>
                          <div className={styles.formGrid}>
                            <Field name="playlistName">
                              {({ input }) => (
                                <TextField
                                  label="Name"
                                  fullWidth
                                  size="small"
                                  {...input}
                                  InputLabelProps={{ shrink: true }}
                                />
                              )}
                            </Field>

                            <Field name="category_id">
                              {({ input }) => (
                                <TextField
                                  select
                                  label="Category"
                                  fullWidth
                                  size="small"
                                  {...input}
                                  InputLabelProps={{ shrink: true }}
                                >
                                  {playlistCategories?.map((category: any) => (
                                    <MenuItem value={category.id} key={category.id}>
                                      {category.category_name}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              )}
                            </Field>
                          </div>

                          <div className={styles.linkedTreatmentsBlock}>
                            <label className={styles.fieldLabel}>Linked Treatments</label>
                            <Autocomplete
                              name="treatment_ids"
                              multiple
                              options={treatmentPlans}
                              getOptionLabel={(option: any) => option.label}
                              getOptionSelected={(option: any, value: any) => option.value === value}
                              initialValue={treatmentPlans.filter((item) => selectedTreatmentIds.includes(item.value))}
                              onChange={handleTreatmentChange}
                              renderInput={(params) => (
                                <TextField {...params} placeholder="Search treatments" size="small" />
                              )}
                            />
                          </div>

                          <div className={styles.visibilityRow}>
                            <FormControl>
                              <RadioGroup row name="options" value={values.options || "user"}>
                                <Field name="options" type="radio" value="user">
                                  {({ input }) => (
                                    <FormControlLabel
                                      control={<Radio {...input} checked={input.checked} size="small" />}
                                      label="User only"
                                    />
                                  )}
                                </Field>
                                <Field name="options" type="radio" value="practice">
                                  {({ input }) => (
                                    <FormControlLabel
                                      control={<Radio {...input} checked={input.checked} size="small" />}
                                      label="Practice"
                                    />
                                  )}
                                </Field>
                              </RadioGroup>
                            </FormControl>
                          </div>

                          <div className={styles.settingsFooter}>
                            <div className={styles.playlistTypePills}>
                              <Chip
                                icon={getPlaylistTypeIcon(currentCategory.type)}
                                label={getLevelLabel(currentCategory.level)}
                                size="small"
                                className={styles.typeChip}
                              />
                              {isArchived && <Chip label="Archived" size="small" className={styles.typeChip} />}
                            </div>

                            <div className={styles.settingsActions}>
                              {currentCategory.type !== "global_playlists" && currentCategory.type !== "non_default_playlists" && (
                                <Button
                                  color="error"
                                  variant="text"
                                  onClick={() => {
                                    confirmAlert({
                                      title: "Delete playlist?",
                                      message: "This will remove the playlist and its configuration for this account.",
                                      buttons: [
                                        { label: "Cancel" },
                                        { label: "Delete", onClick: () => handleDeletePlaylist(currentCategory.id) },
                                      ],
                                    });
                                  }}
                                >
                                  Delete Playlist
                                </Button>
                              )}
                              <Button type="submit" className={styles.saveButton} variant="contained">
                                Save Changes
                              </Button>
                            </div>
                          </div>
                        </form>
                      )}
                    />
                  )}
                </Collapse>
              </section>
            </div>
          </Grid>
        </Grid>
      </Box>

      {currentCategory && (
        <Dialog
          open={openVideoModal}
          onClose={() => setOpenVideoModal(false)}
          PaperProps={{ sx: { width: "640px", borderRadius: "16px", padding: "6px" } }}
        >
          <DialogTitle className={styles.dialogTitle}>Add Video</DialogTitle>
          <DialogContent>
            <div className={styles.dialogForm}>
              <TextField
                label="Video title"
                fullWidth
                size="small"
                value={newVideoForm.title}
                onChange={(e) => setNewVideoForm((prev) => ({ ...prev, title: e.target.value }))}
              />
              <TextField
                label="Video URL"
                fullWidth
                size="small"
                value={newVideoForm.url}
                onChange={(e) => setNewVideoForm((prev) => ({ ...prev, url: e.target.value }))}
              />
              <TextField
                label="Thumbnail URL"
                fullWidth
                size="small"
                value={newVideoForm.thumbnail}
                onChange={(e) => setNewVideoForm((prev) => ({ ...prev, thumbnail: e.target.value }))}
              />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <TextField
                  select
                  label="Platform"
                  fullWidth
                  size="small"
                  value={newVideoForm.platform}
                  onChange={(e) => setNewVideoForm((prev) => ({ ...prev, platform: e.target.value }))}
                >
                  <MenuItem value="youtube">YouTube</MenuItem>
                  <MenuItem value="tiktok">TikTok</MenuItem>
                  <MenuItem value="vimeo">Vimeo</MenuItem>
                  <MenuItem value="s3">Direct / MP4</MenuItem>
                </TextField>
                <TextField
                  label="Duration"
                  fullWidth
                  size="small"
                  value={newVideoForm.duration}
                  onChange={(e) => setNewVideoForm((prev) => ({ ...prev, duration: e.target.value }))}
                />
              </div>
              <TextField
                label="Description"
                fullWidth
                size="small"
                multiline
                minRows={3}
                value={newVideoForm.description}
                onChange={(e) => setNewVideoForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <Button onClick={() => setOpenVideoModal(false)} variant="outlined">Cancel</Button>
            <Button onClick={handleCreateVideo} variant="contained">Add Video</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
